import { useTranslation } from "react-i18next";
import { APlusContentStatus } from "reedy-data/api";
import { Icon } from "~/components/icon/icon";
import { LoadingPanel, ErrorPanel } from "~/components/panels";
import { Text } from "~/components/text";

import { APlusModuleList } from "./a-plus-module-list";
import { actionButton, statePanel } from "./a-plus-content-state";
import { useAmplifyAPlusContent } from "./hooks/use-amplify-a-plus-content";

export const APlusContentTab = () => {
  const { t } = useTranslation(["productDetails"]);
  const { data, error, isPending } = useAmplifyAPlusContent();

  if (isPending) {
    return (
      <LoadingPanel loadingText={t("productDetails:aPlusContent.loading")} />
    );
  }

  if (error) {
    return (
      <ErrorPanel
        title={t("productDetails:aPlusContent.title")}
        errorTitle={t("productDetails:aPlusContent.error.title")}
        errorMessage={t("productDetails:aPlusContent.error.message")}
      />
    );
  }

  if (!data) {
    return statePanel({
      testId: "a-plus-content-not-enabled-panel",
      tone: "neutral",
      icon: "lock",
      title: t("productDetails:aPlusContent.notEnabled.title"),
      text: t("productDetails:aPlusContent.notEnabled.text"),
    });
  }

  const openAmplify = t("productDetails:aPlusContent.openAmplify");
  const viewOnAmazon = t("productDetails:aPlusContent.viewOnAmazon");
  const sourcedFromAmplify = t(
    "productDetails:aPlusContent.sourcedFromAmplify",
  );

  const amplifyButton = (variant: "primary" | "tertiary") =>
    actionButton({
      href: data.amplifyUrl,
      label: openAmplify,
      testId: "a-plus-content-open-amplify",
      variant,
      icon: "sparkles",
    });

  const amazonButton = (variant: "primary" | "tertiary") =>
    actionButton({
      href: data.amazonUrl,
      label: viewOnAmazon,
      testId: "a-plus-content-view-on-amazon",
      variant,
      icon: "square-arrow-out-up-right",
    });

  const sourcedFromFootnote = (
    <Text
      variant="caption"
      className="flex items-center justify-center gap-1.5"
    >
      <Icon name="info" size="small" iconClassName="text-secondary-400" />
      {sourcedFromAmplify}
    </Text>
  );

  if (data.status === APlusContentStatus.PUBLISHED) {
    return statePanel({
      testId: "a-plus-content-published-panel",
      tone: "success",
      title: t("productDetails:aPlusContent.published.title"),
      text: t("productDetails:aPlusContent.published.text"),
      footer:
        data.modules.length > 0 ? (
          <APlusModuleList
            modules={data.modules}
            footer={sourcedFromFootnote}
          />
        ) : (
          sourcedFromFootnote
        ),
      buttons: [amazonButton("primary"), amplifyButton("tertiary")],
    });
  }

  if (data.status === APlusContentStatus.NONE) {
    return statePanel({
      testId: "a-plus-content-panel",
      tone: "warning",
      title: t("productDetails:aPlusContent.empty.title"),
      text: t("productDetails:aPlusContent.empty.text"),
      buttons: [amazonButton("tertiary"), amplifyButton("primary")],
    });
  }

  // UNKNOWN and any unrecognised status: never treat as a definitive none.
  return statePanel({
    testId: "a-plus-content-unknown-panel",
    tone: "neutral",
    title: t("productDetails:aPlusContent.unknown.title"),
    text: t("productDetails:aPlusContent.unknown.text"),
    buttons: [amplifyButton("primary")],
  });
};
